"use client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import Link from "next/link";
// import { Checkbox } from "../ui/checkbox";
// import { Label } from "../ui/label";
import { Typography } from "../ui/typography";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../ui/input-otp";
import useAuth from "@/hooks/use-auth";
import { useAuthDialogStore } from "@/hooks/use-auth-dialog";
import { } from '@aws-amplify/auth'

const formSchema = z.object({
  email: z.string(),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters.",
  }),
  otp: z.string().optional(),
});

const FormSignIn = ({
  setShowOtp,
  showOtp,
}: {
  setShowOtp: Dispatch<SetStateAction<boolean>>;
  showOtp: boolean;
}) => {
  const searchParams = useSearchParams();
  const { signInUser, getCurrentUserInfo, confirmSignUpUser } = useAuth();
  const { signIn, forgotPassword, } = useAuthDialogStore()
  const [loading, setLoading] = useState(false);

  // const [isTermsAndPolicyAccepted, setIsTermsAndPolicyAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: searchParams.get("email") || "",
      password: "",
    },
  });

  const onSubmit = useCallback(async (values: z.infer<typeof formSchema>) => {
    setLoading(true);
    try {
      // console.log("SignIn onSubmit: ", values);
      // setShowOtp(true);
      if (values.otp) {
        const { nextStep, isSignUpComplete } = await confirmSignUpUser(values.email, values.otp)
        console.log("Confirm Sign Up : ", nextStep, isSignUpComplete)
        if (isSignUpComplete) {
          // await autoSignInUser()
          await getCurrentUserInfo()
          toast.success("Email is verified!");
          setShowOtp(false)
          signIn.closeDialog()
        }
      } else {
        const { isSignedIn, nextStep } = await signInUser(values.email, values.password)
        if (isSignedIn) {
          const userInfo = await getCurrentUserInfo();
          // console.log("user info ", userInfo)
          toast.success("Амжилттай!");
          signIn.closeDialog()
        } else {
          if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
            setShowOtp(true)
          }
        }
      }
    } catch (err: any) {
      console.log("err", err.message)
      toast.error(err.message)
    } finally {
      setLoading(false);
    }
  }, [showOtp]);

  const handleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
        {showOtp && (
          <>
            <Typography className="font-semibold">
              Security verification Email
            </Typography>
            <Typography className="font-semibold">
              To secure your account, please complete the following
              verification. We have send verification code to
              <span className="text-primary pl-1">{form.watch("email")}</span>
            </Typography>
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between">
                    <FormLabel>Verification code</FormLabel>
                  </div>
                  <FormControl>
                    <InputOTP
                      maxLength={6}
                      {...field}
                      onChange={(value) => {
                        // Only allow numeric values
                        const numericValue = value.replace(/\D/g, "");
                        field.onChange(numericValue);
                      }}
                      className="w-full"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={0}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={1}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={2}
                        />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={3}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={4}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={5}
                        />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <div>
                    Didn’t get a code?{" "}
                    <span className="font-semibold cursor-pointer">
                      Click to resend
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-3">
              <Button
                type="button"
                className="w-full text-lg font-semibold flex-1"
                variant={"outline"}
                onClick={() => setShowOtp(false)}
              >
                Back
              </Button>
              <Button
                type="submit"
                className="w-full text-lg font-semibold flex-1"
                disabled={loading}
              >
                Continue
              </Button>
            </div>
          </>
        )}
        {!showOtp && (
          <>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Имэйл хаяг</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="your@example.com"
                      {...field}
                      type="email"
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between">
                    <FormLabel>Нууц үг</FormLabel>
                    {/* <Link
                      href={"/auth/forgot-password"}
                      className="flex text-primary underline"
                    >
                      Нууц үг сэргээх ?
                    </Link> */}

                    <span
                      className="flex text-primary underline cursor-pointer"
                      onClick={() => {
                        signIn.closeDialog();
                        forgotPassword.openDialog();
                      }}
                    >
                      Нууц үг сэргээх ?
                    </span>

                  </div>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="********"
                        {...field}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-passwords"
                      />
                      <div
                        className="absolute right-2 top-2 cursor-pointer"
                        onClick={handleShowPassword}
                      >
                        {showPassword ? (
                          <EyeOffIcon size={20} />
                        ) : (
                          <EyeIcon size={20} />
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* <div className="flex cursor-pointer items-center gap-3">
              <Checkbox
                id="remember"
                checked={isTermsAndPolicyAccepted}
                onCheckedChange={(val: boolean) =>
                  setIsTermsAndPolicyAccepted(val)
                }
              />
              <Label
                htmlFor="remember"
                className="text-base font-normal text-nowrap flex flex-wrap"
              >
                By continuing, you agree to our{" "}
                <Link
                  href="/terms/terms.pdf"
                  target="_blank"
                  className="cursor-pointer text-blue-700 underline text-nowrap"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/terms/privacy.pdf"
                  target="_blank"
                  className="cursor-pointer text-blue-700 underline text-nowrap"
                >
                  Privacy Policy
                </Link>
              </Label>
            </div> */}

            <Button
              type="submit"
              className="w-full text-lg font-semibold"
              variant={"default"}
              disabled={loading}
            >
              Continue
            </Button>
          </>
        )}
      </form>
    </Form>
  );
};

export default FormSignIn;
